import {
  FetchState, UserFeedbackState, UserFeedbackEventAction,
  UserFeedbackSaveRequest, UserFeedbackSatisfactionLevel,
  UserFeedbackSaveEventModel,
  UserFeedbackSaveEventWithResponseModel
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import * as datadog from "@/shared/datadog";
import * as RouteNames from "@/employee/route-names";
import * as SharedRoutes from "@/shared/route-names";
import { Config } from "@/shared/config";
import { defineStore } from "pinia";
import _ from "lodash";

export const userFeedbackTriggerRoutes: string[] = Object.keys(UserFeedbackEventAction)
    .filter(k => UserFeedbackEventAction[k] !== UserFeedbackEventAction.None)
    .map(k => (mapToRouteName(UserFeedbackEventAction[k])));

function mapToRouteName (actionType: UserFeedbackEventAction) {
    switch (actionType) {
        case UserFeedbackEventAction.AccountSummary:
        case UserFeedbackEventAction.TaxForm:
            return RouteNames.ResourcesDocuments;
        case UserFeedbackEventAction.AddExpense:
        case UserFeedbackEventAction.UploadReceipt:
            return RouteNames.HealthcareExpenses;
        case UserFeedbackEventAction.AddRecurringContribution:
        case UserFeedbackEventAction.EditRecurringContribution:
        case UserFeedbackEventAction.SingleContribution:
            return RouteNames.Contribution;
        case UserFeedbackEventAction.ProductScanner:
            return RouteNames.ProductScanner;
        case UserFeedbackEventAction.RequestReimbursement:
            return RouteNames.ReimburseMe;
        case UserFeedbackEventAction.MyProfile:
        case UserFeedbackEventAction.EditProfile:
        case UserFeedbackEventAction.AddAuthorizedSigner:
        case UserFeedbackEventAction.UpdateAuthorizedSigner:
        case UserFeedbackEventAction.AddBeneficiary:
        case UserFeedbackEventAction.AddDependent:
        case UserFeedbackEventAction.UpdateDependent:
            return RouteNames.MyProfileDisplay;
        case UserFeedbackEventAction.Security:
            return SharedRoutes.Security;
        case UserFeedbackEventAction.BiometricSettings:
            return RouteNames.BiometricAuthSettings;
        case UserFeedbackEventAction.ContributionPlanner:
            return RouteNames.ContributionPlanner;
        default:
            break;
    }
}

const positiveRatings =
  [ UserFeedbackSatisfactionLevel.Neutral,
    UserFeedbackSatisfactionLevel.Satisfied,
    UserFeedbackSatisfactionLevel.VerySatisfied
  ];

export const initialState: UserFeedbackState = {
  triggerActions: [],
  userFeedbackActions: [],
  shouldPrompt: false,
  fetchState: FetchState.Uninitialized,
  isRouteReady: false,
  userFeedback: null
};


export const useUserFeedbackStore = defineStore("userFeedback", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("userFeedback"),
    },
    state: (): UserFeedbackState => (_.cloneDeep(initialState)),
    getters: {
        canShowPrompt: state =>
            state.fetchState === FetchState.Loaded
            && state.userFeedbackActions.length > 0
            && state.shouldPrompt
            && state.isRouteReady
            && state.triggerActions.filter(value => state.userFeedbackActions.includes(value)).length > 0,
        showAppRatePrompt: state =>
            Boolean(Config.Mobile.RateApp) === true
            && state.userFeedback
            && positiveRatings.includes(state.userFeedback.satisfactionLevel)
    },
    actions: {
        setTriggerActions (actions: UserFeedbackEventAction[]) {
            this.triggerActions = actions;
        },
        setShouldPrompt (shouldPrompt: boolean) {
            this.shouldPrompt = shouldPrompt;
        },
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setUserFeedbackAction (action) {
            if (!action) return;
            this.userFeedbackActions.push(action);
        },
        setIsRouteReady (value) {
            // Set it to true onlyif userFeedbackActions has value.
            // Prompt when they leave the feedback action route
            if (this.userFeedbackActions.length > 0) {
                this.isRouteReady = value;
            }
        },
        setUserFeedback (value) {
          this.userFeedback = value;
        },
        getUserFeedbackTriggers (): Promise<void> {
            return UserService.getUserFeedbackTriggers()
                .then(response => {
                    const actions = response.data.eligibleActions;
                    this.setTriggerActions(actions);
                    if (actions !== undefined && actions.length > 0) {
                        this.setShouldPrompt(true);
                    }
                    this.setFetchState(FetchState.Loaded);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        saveFeedbackEvent (request: UserFeedbackSaveEventModel) {
            if (!request || !request.response) {
                datadog.log("SaveFeedbackEvent failed. Invalid request.", {}, "error");
                return;
            }
            const userFeedbackSaveRequest: UserFeedbackSaveRequest = {
                response: request.response,
                action: this.userFeedbackActions[0]
            };
            this.save(userFeedbackSaveRequest);
        },
        saveFeedbackEventWithResponse (request: UserFeedbackSaveEventWithResponseModel) {
            if (!request || !request.response || !request.userFeedback) {
                datadog.log("SaveFeedbackEventWithResponse failed. Invalid request.", {}, "error");
                return;
            }
            const userFeedbackSaveRequest: UserFeedbackSaveRequest = {
                response: request.response,
                userFeedback: request.userFeedback,
                action: this.userFeedbackActions[0]
            };
            this.setUserFeedback(request.userFeedback);
            this.save(userFeedbackSaveRequest);
        },
        save (request: UserFeedbackSaveRequest) {
            if (!request) {
                datadog.log("Save failed. Invalid request.", {}, "error");
                return;
            }
            // hide prompt window on save irrespective of api response status.
            this.setShouldPrompt(false);
            this.setIsRouteReady(false);
            UserService.saveUserFeedback(request)
                .catch(err => {
                    datadog.log(err.message, {}, "error");
                });
        },
    }
});